export const ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE_VARS = [
  'supplierName',
  'blNumber',
  'blDate',
  'expenseReference',
  'establishmentName',
] as const

export type AccountingSupplierEmailTemplateVar = (typeof ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE_VARS)[number]

export interface AccountingSupplierEmailTemplateValues {
  supplierName?: string
  blNumber?: string
  blDate?: string
  expenseReference?: string
  establishmentName?: string
}

export interface AccountingSupplierEmailTemplateSettings {
  subject: string
  greeting: string
  message: string
  closing: string
  includeSignature: boolean
  defaultCc: string[]
}

export interface AccountingSupplierEmailEstablishment {
  name?: string
  fiscalName?: string
  taxId?: string
  phone?: string
}

export const ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_PREFIX = 'ELFARABI - '
export const ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_MAX_LENGTH = 200

const ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_PREFIX_RE = /^(?:elfarabi\s*-\s*)+/i

export function stripAccountingSupplierEmailSubjectPrefix(subject: string): string {
  return String(subject || '')
    .replace(/^\s+/, '')
    .replace(ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_PREFIX_RE, '')
    .replace(/^\s+/, '')
}

export function normalizeAccountingSupplierEmailSubject(subject: string): string {
  const rest = stripAccountingSupplierEmailSubjectPrefix(subject)
  const prefixed = `${ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_PREFIX}${rest}`
  if (prefixed.length <= ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_MAX_LENGTH) return prefixed
  return prefixed.slice(0, ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_MAX_LENGTH)
}

export const DEFAULT_ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE: AccountingSupplierEmailTemplateSettings = {
  subject: `${ACCOUNTING_SUPPLIER_EMAIL_SUBJECT_PREFIX}Relance de facture fournisseur — BL {{blNumber}} — {{establishmentName}}`,
  greeting: 'Madame, Monsieur,',
  message:
    "Nous nous adressons à vous au sujet d'une facture fournisseur manquante.\nMerci de bien vouloir nous transmettre la facture correspondant au bon de livraison {{blNumber}} en date du {{blDate}}.\nRéférence de dépense Maarefati : {{expenseReference}}.",
  closing: 'Cordialement,',
  includeSignature: true,
  defaultCc: [],
}

function escapeHtml(value: string): string {
  return String(value || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function stripTags(value: string): string {
  return String(value || '').replace(/<[^>]*>/g, '')
}

function sanitizeTemplateText(value: unknown, fallback = ''): string {
  return stripTags(String(value ?? fallback)).replace(/\u0000/g, '').trimEnd()
}

export function substituteAccountingSupplierEmailTemplate(
  template: string,
  values: AccountingSupplierEmailTemplateValues,
  options: {html?: boolean} = {}
): string {
  const allowed = new Set<string>(ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE_VARS)
  const replaced = stripTags(String(template || '')).replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_match, rawName: string) => {
    const name = String(rawName || '')
    if (!allowed.has(name)) return ''
    const value = String(values[name as AccountingSupplierEmailTemplateVar] || '')
    return options.html ? escapeHtml(value) : value
  })
  return replaced.replace(/\{\{[\s\S]*?\}\}/g, '')
}

export function establishmentSignatureLines(
  establishment?: AccountingSupplierEmailEstablishment | null,
  signatureLines?: string[] | null
): string[] {
  const provided = (signatureLines || []).map((line) => String(line || '').trimEnd()).filter((line) => line !== '')
  if (provided.length) {
    const unique: string[] = []
    const seen = new Set<string>()
    for (const line of provided) {
      const key = line.replace(/\s+/g, ' ').trim().toLowerCase()
      if (!key || seen.has(key)) continue
      seen.add(key)
      unique.push(line)
    }
    return unique
  }
  const lines = [
    establishment?.fiscalName || establishment?.name,
    establishment?.taxId ? `MF : ${establishment.taxId}` : '',
    establishment?.phone || '',
  ]
    .map((line) => String(line || '').trim())
    .filter(Boolean)
  return lines
}

export function stripEmbeddedAccountingEmailSignature(message: string, signatureLines?: string[] | null): string {
  let next = String(message || '').replace(/\s+$/g, '')
  const lines = establishmentSignatureLines(undefined, signatureLines)
  if (!lines.length) return next.replace(/^\s+|\s+$/g, '')
  const block = lines.join('\n')
  if (next.endsWith(block)) {
    next = next.slice(0, next.length - block.length).replace(/\s+$/g, '')
  }
  const lastLine = lines[lines.length - 1]
  if (lastLine && next.endsWith(lastLine)) {
    const idx = next.lastIndexOf(lines[0] || lastLine)
    if (idx > 0) next = next.slice(0, idx).replace(/\s+$/g, '')
  }
  return next.replace(/^\s+|\s+$/g, '')
}

function paragraphsFromText(text: string): string[] {
  return String(text || '')
    .replace(/\r\n/g, '\n')
    .split(/\n{2,}/)
    .map((block) => block.replace(/[ \t]+\n/g, '\n').trim())
    .filter(Boolean)
}

function htmlParagraphs(text: string): string {
  return paragraphsFromText(text)
    .map((block) => {
      const html = escapeHtml(block).replace(/\n/g, '<br>\n')
      return `<p style="margin:0 0 12px 0;padding:0;text-align:left;">${html}</p>`
    })
    .join('\n')
}

export function renderAccountingSupplierReminderText(input: {
  greeting?: string
  message: string
  closing?: string
  includeSignature?: boolean
  signatureLines?: string[] | null
  establishment?: AccountingSupplierEmailEstablishment | null
}): string {
  const parts = [input.greeting, input.message, input.closing]
    .map((part) => String(part || '').trim())
    .filter(Boolean)
  const signature =
    input.includeSignature === false ? [] : establishmentSignatureLines(input.establishment, input.signatureLines)
  const body = parts.join('\n\n')
  if (!signature.length) return body
  if (body.includes(signature.join('\n'))) return body
  return body ? `${body}\n\n${signature.join('\n')}` : signature.join('\n')
}

export function renderAccountingSupplierReminderHtml(input: {
  greeting?: string
  message: string
  closing?: string
  includeSignature?: boolean
  signatureLines?: string[] | null
  establishment?: AccountingSupplierEmailEstablishment | null
}): string {
  const greeting = String(input.greeting || '').trim()
  const closing = String(input.closing || '').trim()
  const message = String(input.message || '').trim()
  const signature =
    input.includeSignature === false ? [] : establishmentSignatureLines(input.establishment, input.signatureLines)
  const signatureHtml = signature.length
    ? `<p style="margin:16px 0 0 0;padding:0;text-align:left;">${signature.map((line) => escapeHtml(line)).join('<br>\n')}</p>`
    : ''
  const inner = [
    greeting ? htmlParagraphs(greeting) : '',
    message ? htmlParagraphs(message) : '',
    closing ? htmlParagraphs(closing) : '',
    signatureHtml,
  ]
    .filter(Boolean)
    .join('\n')
  return [
    '<!DOCTYPE html>',
    '<html lang="fr">',
    '<head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"></head>',
    '<body style="margin:0;padding:0;background:#ffffff;">',
    '<table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#ffffff;border-collapse:collapse;">',
    '<tr><td align="left" style="padding:16px 20px;font-family:Arial,Helvetica,sans-serif;font-size:15px;line-height:1.5;color:#1f2937;text-align:left;background:#ffffff;">',
    inner,
    '</td></tr></table>',
    '</body></html>',
  ].join('')
}

export function countAccountingEmailSignatureBlocks(html: string, signatureLines?: string[] | null): number {
  const first = (signatureLines || []).map((line) => String(line || '').trim()).find(Boolean)
  if (!first) return 0
  const needle = escapeHtml(first)
  if (!needle) return 0
  return String(html || '').split(needle).length - 1
}

export function parseAccountingSupplierEmailTemplateSettings(
  payload: unknown
): AccountingSupplierEmailTemplateSettings {
  const root = payload && typeof payload === 'object' && !Array.isArray(payload) ? (payload as Record<string, unknown>) : {}
  const nested =
    root.template && typeof root.template === 'object' && !Array.isArray(root.template)
      ? (root.template as Record<string, unknown>)
      : root.settings && typeof root.settings === 'object' && !Array.isArray(root.settings)
        ? (root.settings as Record<string, unknown>)
        : root
  const includeRaw = nested.includeSignature
  const rawSubject =
    sanitizeTemplateText(nested.subject, DEFAULT_ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE.subject) ||
    DEFAULT_ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE.subject
  return {
    subject: normalizeAccountingSupplierEmailSubject(rawSubject),
    greeting: sanitizeTemplateText(nested.greeting, DEFAULT_ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE.greeting),
    message: sanitizeTemplateText(nested.message, DEFAULT_ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE.message),
    closing: sanitizeTemplateText(nested.closing, DEFAULT_ACCOUNTING_SUPPLIER_EMAIL_TEMPLATE.closing),
    includeSignature: includeRaw === false || includeRaw === 'false' ? false : true,
    defaultCc: Array.isArray(nested.defaultCc)
      ? nested.defaultCc.map((item) => String(item || '').trim()).filter(Boolean).slice(0, 5)
      : String(nested.defaultCc || '')
          .split(/[,;]/)
          .map((item) => item.trim())
          .filter(Boolean)
          .slice(0, 5),
  }
}

export function buildAccountingSupplierEmailTemplatePayload(
  settings: AccountingSupplierEmailTemplateSettings
): AccountingSupplierEmailTemplateSettings {
  return parseAccountingSupplierEmailTemplateSettings(settings)
}

export function normalizeAccountingEmailReminderSendPayload<T extends {subject?: string}>(
  body: T
): T {
  return {
    ...body,
    subject: normalizeAccountingSupplierEmailSubject(String(body?.subject || '')),
  }
}

export function renderConfiguredSupplierReminder(input: {
  settings: AccountingSupplierEmailTemplateSettings
  values: AccountingSupplierEmailTemplateValues
  establishment?: AccountingSupplierEmailEstablishment | null
  signatureLines?: string[] | null
}): {subject: string; message: string; html: string; text: string} {
  const settings = parseAccountingSupplierEmailTemplateSettings(input.settings)
  const greeting = substituteAccountingSupplierEmailTemplate(settings.greeting, input.values)
  const message = substituteAccountingSupplierEmailTemplate(settings.message, input.values)
  const closing = substituteAccountingSupplierEmailTemplate(settings.closing, input.values)
  const subject = normalizeAccountingSupplierEmailSubject(
    substituteAccountingSupplierEmailTemplate(settings.subject, input.values)
  )
  const bodyInput = {
    greeting,
    message,
    closing,
    includeSignature: settings.includeSignature,
    signatureLines: input.signatureLines,
    establishment: input.establishment,
  }
  return {
    subject,
    message: [greeting, message, closing].filter((part) => String(part || '').trim()).join('\n\n'),
    html: renderAccountingSupplierReminderHtml(bodyInput),
    text: renderAccountingSupplierReminderText(bodyInput),
  }
}
